/* IndexedDB-backed offline store for Canales exams.
   - exams: cached GET /api/public/exam/{slug} payloads keyed by slug
   - queue: pending POST submissions to be flushed when online
*/
import { API } from "@/api/client";

const DB_NAME = "canales-offline";
const DB_VERSION = 1;
const STORE_EXAMS = "exams";
const STORE_QUEUE = "queue";

let dbPromise = null;

function openDB() {
  if (dbPromise) return dbPromise;
  dbPromise = new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE_EXAMS)) {
        db.createObjectStore(STORE_EXAMS, { keyPath: "slug" });
      }
      if (!db.objectStoreNames.contains(STORE_QUEUE)) {
        const os = db.createObjectStore(STORE_QUEUE, { keyPath: "id", autoIncrement: true });
        os.createIndex("slug", "slug", { unique: false });
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
  return dbPromise;
}

async function tx(storeName, mode, fn) {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const t = db.transaction(storeName, mode);
    const store = t.objectStore(storeName);
    let result;
    Promise.resolve(fn(store)).then((r) => { result = r; }).catch(reject);
    t.oncomplete = () => resolve(result);
    t.onerror = () => reject(t.error);
    t.onabort = () => reject(t.error);
  });
}

/* Exams cache */
export async function saveExamCache(slug, payload) {
  try {
    await tx(STORE_EXAMS, "readwrite", (store) =>
      store.put({ slug, payload, cached_at: Date.now() })
    );
  } catch (e) {
    // ignore — offline cache is best-effort
  }
}

export async function getExamCache(slug) {
  try {
    return await tx(STORE_EXAMS, "readonly", (store) => {
      return new Promise((resolve) => {
        const r = store.get(slug);
        r.onsuccess = () => resolve(r.result?.payload || null);
        r.onerror = () => resolve(null);
      });
    });
  } catch {
    return null;
  }
}

/* Queue of pending submissions */
export async function enqueueSubmission({ slug, payload }) {
  return tx(STORE_QUEUE, "readwrite", (store) => {
    return new Promise((resolve) => {
      const r = store.add({ slug, payload, queued_at: Date.now() });
      r.onsuccess = () => resolve(r.result);
      r.onerror = () => resolve(null);
    });
  });
}

export async function getQueue() {
  try {
    return await tx(STORE_QUEUE, "readonly", (store) => {
      return new Promise((resolve) => {
        const r = store.getAll();
        r.onsuccess = () => resolve(r.result || []);
        r.onerror = () => resolve([]);
      });
    });
  } catch {
    return [];
  }
}

export async function removeFromQueue(id) {
  return tx(STORE_QUEUE, "readwrite", (store) => {
    return new Promise((resolve) => {
      const r = store.delete(id);
      r.onsuccess = () => resolve(true);
      r.onerror = () => resolve(false);
    });
  });
}

export async function queueCount() {
  const items = await getQueue();
  return items.length;
}

/* Flush queued submissions. Uses plain fetch to avoid axios interceptors
   attempting anything fancy and to give us a raw ok flag per attempt. */
let syncing = false;
export async function syncPending({ onProgress } = {}) {
  if (syncing) return { attempted: 0, sent: 0, failed: 0, results: [] };
  if (typeof navigator !== "undefined" && navigator.onLine === false) {
    return { attempted: 0, sent: 0, failed: 0, results: [] };
  }
  syncing = true;
  const results = [];
  let sent = 0;
  let failed = 0;
  try {
    const items = await getQueue();
    for (const item of items) {
      try {
        const resp = await fetch(`${API}/public/exam/${item.slug}/submit`, {
          method: "POST",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(item.payload),
        });
        if (resp.ok) {
          const data = await resp.json().catch(() => null);
          await removeFromQueue(item.id);
          sent += 1;
          results.push({ id: item.id, slug: item.slug, ok: true, data });
        } else {
          failed += 1;
          results.push({ id: item.id, slug: item.slug, ok: false, status: resp.status });
        }
      } catch (e) {
        failed += 1;
        results.push({ id: item.id, slug: item.slug, ok: false, error: String(e) });
      }
      if (onProgress) onProgress({ sent, failed });
    }
    return { attempted: items.length, sent, failed, results };
  } finally {
    syncing = false;
  }
}
